import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type MythicNarratorMode = "ai" | "procedural" | "hybrid";
export type MythicCombatDirectorMode = "player_command_dm_control" | "manual";
export type MythicCombatPacing = "cinematic" | "balanced" | "fast";
export type MythicPlayerTurnIdlePolicy = "auto_attack" | "wait";

export interface MythicRuntimeSettings {
  compactNarration: boolean;
  chatAutoFollow: boolean;
  narratorMode: MythicNarratorMode;
  combatDirectorMode: MythicCombatDirectorMode;
  combatPacing: MythicCombatPacing;
  playerTurnIdlePolicy: MythicPlayerTurnIdlePolicy;
  playerTurnTimeoutMs: number;
}

interface SettingsPanelProps {
  settings: MythicRuntimeSettings;
  onSettingsChange: (next: MythicRuntimeSettings) => void;
  voiceEnabled: boolean;
  voiceValue: string;
  voiceSupported: boolean;
  voiceBlocked: boolean;
  onToggleVoice: (enabled: boolean) => void;
  onVoiceChange: (voice: string) => void;
  onSpeakLatest: () => void;
  onStopVoice: () => void;
  narratorMode: MythicNarratorMode;
  onNarratorModeChange: (mode: MythicNarratorMode) => void;
}

export function SettingsPanel(props: SettingsPanelProps) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border bg-background/30 p-3">
        <div className="mb-2 text-sm font-semibold">DM Voice</div>
        <div className="mb-2 flex flex-wrap items-center gap-3 text-xs">
          <div className="flex items-center gap-2">
            <Switch
              checked={props.voiceEnabled}
              onCheckedChange={props.onToggleVoice}
              disabled={!props.voiceSupported}
              aria-label="Toggle DM voice"
            />
            <span className="text-muted-foreground">{props.voiceEnabled ? "Voice enabled" : "Voice muted"}</span>
          </div>
          <Button size="sm" variant="secondary" onClick={props.onSpeakLatest} disabled={!props.voiceSupported}>
            Speak latest
          </Button>
          <Button size="sm" variant="outline" onClick={props.onStopVoice} disabled={!props.voiceSupported}>
            Stop
          </Button>
        </div>
        <div className="mb-2 grid max-w-[220px] gap-1 text-xs">
          <span className="text-muted-foreground">Voice profile</span>
          <Select value={props.voiceValue} onValueChange={props.onVoiceChange} disabled={!props.voiceSupported}>
            <SelectTrigger className="h-8 border-amber-200/20 bg-background/30 text-xs">
              <SelectValue placeholder="Choose voice" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alloy">Alloy (Male)</SelectItem>
              <SelectItem value="verse">Verse (Male Alt)</SelectItem>
              <SelectItem value="nova">Nova</SelectItem>
              <SelectItem value="aria">Aria</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {!props.voiceSupported ? (
          <div className="text-xs text-muted-foreground">Voice is unavailable in this browser/runtime.</div>
        ) : null}
        {props.voiceBlocked ? (
          <div className="text-xs text-amber-200">Playback was blocked. Click Speak latest after interacting with the page.</div>
        ) : null}
      </div>

      <div className="rounded-lg border border-border bg-background/30 p-3">
        <div className="mb-2 text-sm font-semibold">Narration</div>
        <div className="flex items-center gap-2 text-xs">
          <Switch
            checked={props.settings.compactNarration}
            onCheckedChange={() => {}}
            disabled
            aria-label="Compact narration mode"
          />
          <span className="text-muted-foreground">Compact narration (server-locked default)</span>
        </div>
        <div className="mt-3 grid max-w-[220px] gap-1 text-xs">
          <span className="text-muted-foreground">Narrator mode</span>
          <Select value={props.narratorMode} onValueChange={(value) => props.onNarratorModeChange(value as MythicNarratorMode)}>
            <SelectTrigger className="h-8 border-amber-200/20 bg-background/30 text-xs">
              <SelectValue placeholder="Choose narrator mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hybrid">Hybrid (Auto)</SelectItem>
              <SelectItem value="procedural">Procedural Only</SelectItem>
              <SelectItem value="ai">AI Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-background/30 p-3">
        <div className="mb-2 text-sm font-semibold">Combat Director</div>
        <div className="grid gap-3 text-xs">
          <div className="grid max-w-[260px] gap-1">
            <span className="text-muted-foreground">Control mode</span>
            <Select
              value={props.settings.combatDirectorMode}
              onValueChange={(value) =>
                props.onSettingsChange({
                  ...props.settings,
                  combatDirectorMode: value as MythicCombatDirectorMode,
                })}
            >
              <SelectTrigger className="h-8 border-amber-200/20 bg-background/30 text-xs">
                <SelectValue placeholder="Choose control mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="player_command_dm_control">Player command + DM control</SelectItem>
                <SelectItem value="manual">Manual combat pacing</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid max-w-[220px] gap-1">
            <span className="text-muted-foreground">Pacing</span>
            <Select
              value={props.settings.combatPacing}
              onValueChange={(value) =>
                props.onSettingsChange({
                  ...props.settings,
                  combatPacing: value as MythicCombatPacing,
                })}
            >
              <SelectTrigger className="h-8 border-amber-200/20 bg-background/30 text-xs">
                <SelectValue placeholder="Choose pacing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cinematic">Cinematic</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="fast">Fast</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid max-w-[220px] gap-1">
            <span className="text-muted-foreground">Player idle policy</span>
            <Select
              value={props.settings.playerTurnIdlePolicy}
              onValueChange={(value) =>
                props.onSettingsChange({
                  ...props.settings,
                  playerTurnIdlePolicy: value as MythicPlayerTurnIdlePolicy,
                })}
            >
              <SelectTrigger className="h-8 border-amber-200/20 bg-background/30 text-xs">
                <SelectValue placeholder="Choose idle behavior" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto_attack">Auto-attack timeout</SelectItem>
                <SelectItem value="wait">Wait for player command</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid max-w-[240px] gap-1">
            <span className="text-muted-foreground">Idle timeout (seconds)</span>
            <input
              type="number"
              min={2}
              max={30}
              step={1}
              className="h-8 rounded border border-amber-200/20 bg-background/30 px-2 text-xs text-foreground"
              value={Math.max(2, Math.floor(props.settings.playerTurnTimeoutMs / 1000))}
              onChange={(event) => {
                const seconds = Number(event.target.value);
                const clampedSeconds = Number.isFinite(seconds)
                  ? Math.max(2, Math.min(30, Math.floor(seconds)))
                  : 7;
                props.onSettingsChange({
                  ...props.settings,
                  playerTurnTimeoutMs: clampedSeconds * 1000,
                });
              }}
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-background/30 p-3">
        <div className="mb-2 text-sm font-semibold">Chat Follow Behavior</div>
        <div className="flex items-center gap-2 text-xs">
          <Switch
            checked={props.settings.chatAutoFollow}
            onCheckedChange={(checked) =>
              props.onSettingsChange({
                ...props.settings,
                chatAutoFollow: checked,
              })}
            aria-label="Auto-follow latest DM messages"
          />
          <span className="text-muted-foreground">
            {props.settings.chatAutoFollow
              ? "Auto-follow latest narration when near bottom"
              : "Manual follow (use Jump to latest)"}
          </span>
        </div>
      </div>
    </div>
  );
}
